import React, { useState, useRef, useEffect } from "react";
import styles from "./EditUser.module.css";
import useIsMobile from "../../hooks/useIsMobile";

interface CreateFuelCodeModalProps {
  open: boolean;
  onCancel: () => void;
  onSubmit: (data: {
    carNumber: string;
    fullName: string;
    idNumber: string;
    altCarNumber: string;
    reason: string;
  }) => void;
  userPhone: string;
  loading?: boolean;
  error?: string;
}

const INITIAL_LEFT = typeof window !== "undefined" ? Math.max(20, window.innerWidth - 420) : 300;
const INITIAL_TOP = 80;

const CreateFuelCodeModal: React.FC<CreateFuelCodeModalProps> = ({
  open,
  onCancel,
  onSubmit,
  userPhone,
  loading = false,
  error = "",
}) => {
  const [formData, setFormData] = useState({
    carNumber: "",
    fullName: "",
    idNumber: "",
    altCarNumber: "",
    reason: "",
  });

  const isMobile = useIsMobile();
  const [position, setPosition] = useState({ x: INITIAL_LEFT, y: INITIAL_TOP });
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef<{ mouseX: number; mouseY: number; panelX: number; panelY: number } | null>(null);

  useEffect(() => {
    if (isMobile) return;
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!dragStartRef.current) return;
      const { mouseX, mouseY, panelX, panelY } = dragStartRef.current;
      setPosition({
        x: panelX + (e.clientX - mouseX),
        y: panelY + (e.clientY - mouseY),
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      dragStartRef.current = null;
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, isMobile]);

  const handleHeaderMouseDown = (e: React.MouseEvent) => {
    if (loading || isMobile) return;
    e.preventDefault();
    dragStartRef.current = {
      mouseX: e.clientX,
      mouseY: e.clientY,
      panelX: position.x,
      panelY: position.y,
    };
    setIsDragging(true);
  };

  const handleFormChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const handleCancel = () => {
    onCancel();
    setFormData({
      carNumber: "",
      fullName: "",
      idNumber: "",
      altCarNumber: "",
      reason: "",
    });
  };

  if (!open) return null;

  const panelStyle: React.CSSProperties = isMobile
    ? {
        position: "relative",
        background: "#fff",
        padding: 0,
        borderRadius: 8,
        width: "min(420px, calc(100vw - 32px))",
        maxHeight: "90vh",
        overflowY: "auto",
        textAlign: "right",
        direction: "rtl",
        boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
      }
    : {
        position: "fixed",
        left: position.x,
        top: position.y,
        zIndex: 3000,
        background: "#fff",
        padding: 0,
        borderRadius: 8,
        minWidth: 350,
        textAlign: "right",
        direction: "rtl",
        boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
      };

  const headerCursor = loading || isMobile ? "default" : "move";

  const panel = (
    <div style={panelStyle} onClick={isMobile ? (e) => e.stopPropagation() : undefined}>
      <div
        onMouseDown={handleHeaderMouseDown}
        style={{
          padding: "12px 24px",
          paddingLeft: 48,
          cursor: headerCursor,
          userSelect: "none",
          borderBottom: "1px solid #eee",
          backgroundColor: "#f8f9fa",
          borderTopLeftRadius: 8,
          borderTopRightRadius: 8,
          position: "relative",
        }}
      >
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            handleCancel();
          }}
          onMouseDown={(e) => e.stopPropagation()}
          disabled={loading}
          aria-label="Close"
          style={{
            position: "absolute",
            top: 0,
            left: 5,
            background: "none",
            border: "none",
            fontSize: 24,
            lineHeight: 1,
            cursor: loading ? "default" : "pointer",
            color: "#666",
            padding: 4,
            minWidth: 24,
          }}
        >
          ×
        </button>
        <div style={{ fontWeight: 600, fontSize: 18 }}>
          הפקת מספר תדלוק עבור {userPhone}
        </div>
        {!isMobile && (
          <div style={{ fontSize: 12, color: "#888", marginTop: 4 }}>
            גרור להזזה
          </div>
        )}
      </div>
      <form
        onSubmit={handleSubmit}
        style={{
          padding: 24,
        }}
      >
        <div style={{ marginBottom: 12 }}>
          <label>מספר רכב צמוד</label>
          <input
            name="carNumber"
            value={formData.carNumber}
            onChange={handleFormChange}
            style={{ width: "100%", marginTop: 4 }}
            disabled={loading}
          />
        </div>
        <div style={{ marginBottom: 12 }}>
          <label>שם מלא</label>
          <input
            name="fullName"
            value={formData.fullName}
            onChange={handleFormChange}
            style={{ width: "100%", marginTop: 4 }}
            disabled={loading}
          />
        </div>
        <div style={{ marginBottom: 12 }}>
          <label>תעודת זהות</label>
          <input
            name="idNumber"
            value={formData.idNumber}
            onChange={handleFormChange}
            style={{ width: "100%", marginTop: 4 }}
            disabled={loading}
          />
        </div>
        <div style={{ marginBottom: 12 }}>
          <label>מספר רכב חלופי</label>
          <input
            name="altCarNumber"
            value={formData.altCarNumber}
            onChange={handleFormChange}
            style={{ width: "100%", marginTop: 4 }}
            disabled={loading}
          />
        </div>
        <div style={{ marginBottom: 12 }}>
          <label>סיבת החלפת הרכב</label>
          <textarea
            name="reason"
            value={formData.reason}
            onChange={handleFormChange}
            style={{ width: "100%", marginTop: 4 }}
            rows={2}
            disabled={loading}
          />
        </div>
        {error && (
          <div style={{ color: "red", fontSize: 16, marginBottom: 8 }}>
            {error}
          </div>
        )}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 10,
            marginTop: 16,
          }}
        >
          <button
            type="submit"
            style={{ minWidth: 80 }}
            className={styles.confirmButton}
            disabled={loading}
          >
            {loading ? "שולח..." : "שלח"}
          </button>
          <button
            type="button"
            onClick={handleCancel}
            style={{ minWidth: 80 }}
            className={styles.cancelButton}
            disabled={loading}
          >
            ביטול
          </button>
        </div>
      </form>
    </div>
  );

  if (isMobile) {
    return (
      <div
        onClick={loading ? undefined : handleCancel}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.4)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 3000,
          padding: 16,
        }}
      >
        {panel}
      </div>
    );
  }

  return panel;
};

export default CreateFuelCodeModal;
